import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";

// Material Dashboard 2 React components
import MDBox from "../../MDBox";
import MDTypography from "../../MDTypography";
import { useCimbeo } from "../../../context";

// Material Dashboard 2 React example components
import Breadcrumbs from "../../../examples/Breadcrumbs";

function TopNavBar({ light }) {
  const [state, setState] = useState({
    openMenu: false,
    scrolled: false
  });
  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();

  const {projectName, user, handleLogout} = useCimbeo();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0;
      setState(prev => ({ ...prev, scrolled: isScrolled }));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleOpenMenu = (event) => setState(prev => ({ ...prev, openMenu: event.currentTarget }));
  const handleCloseMenu = () => setState(prev => ({ ...prev, openMenu: false }));

  const handleLogoutClick = () => {
    handleLogout();
    handleCloseMenu();
    navigate("/authentication/sign-in");
  };

  // Render the profile menu
  const renderMenu = () => (
    <Menu
      anchorEl={state.openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      open={Boolean(state.openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <MDBox px={2} py={1}>
        <Typography variant="body2" sx={{ cursor: 'pointer', mb: 1 }}>Profile</Typography>
        <Typography variant="body2" sx={{ cursor: 'pointer', mb: 1 }}>Settings</Typography>
        <Typography 
          variant="body2" 
          sx={{ cursor: 'pointer', color: 'error.main' }}
          onClick={handleLogoutClick}
        >
          Logout
        </Typography>
      </MDBox>
    </Menu>
  );

  return (
    <AppBar
      position="static"
      color={state.scrolled ? "default" : "transparent"}
      elevation={state.scrolled ? 1 : 0}
      sx={{
        backgroundColor: state.scrolled ? "navy.main" : "transparent",
        backdropFilter: state.scrolled ? "none" : "blur(8px)",
        borderBottom: state.scrolled 
          ? "1px solid rgba(0, 0, 0, 0.12)"
          : "1px solid rgba(255, 255, 255, 0.1)",
        transition: "all 0.3s ease-in-out",
        borderRadius: "12px",
        marginBottom: "16px",
        width: "100%",
      }}
    >
      <Toolbar>
        <MDBox sx={{ flexGrow: 0, width: "250px" }}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        <MDBox sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
          {projectName && (
            <MDTypography 
              variant="h2" 
              sx={{ 
                transition: "color 0.3s ease-in-out",
                fontWeight: "bold"
              }}
            >
              {projectName}
            </MDTypography>
          )}
        </MDBox>
        <MDBox sx={{ width: "250px", display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            size="small"
            disableRipple
            color="inherit"
            aria-controls="profile-menu"
            aria-haspopup="true"
            onClick={handleOpenMenu}
          >
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDTypography 
                variant="body2" 
                sx={{ 
                  transition: "color 0.3s ease-in-out"
                }}
              >
                {user.firstName + " " + user.lastName}
              </MDTypography>
              <Avatar
                src={user.photo}
                alt={user.firstName[0] + user.lastName[0]}
                sx={{ width: 32, height: 32 }}
              >
                {user.firstName[0] + user.lastName[0]}
              </Avatar>
            </MDBox>
          </IconButton>
          {renderMenu()}
        </MDBox>
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of TopNavBar
TopNavBar.defaultProps = {
  light: false,
};

// Typechecking props for the TopNavBar
TopNavBar.propTypes = {
  light: PropTypes.bool,
};

export default TopNavBar;
